/**
 * "Is this a different order?" — asked when a CSR changes a draft's customer
 * or PO number away from the PO the draft was generated from (kanban
 * 3019f212, driver AU01595: one email carried two POs and the CSR correctly
 * re-keyed the draft into the second one).
 *
 * The answer is deterministic and recorded server-side:
 *   - "Yes, different order" -> POST detach-source-identity, then save. The
 *     Sage confirmation verifier stops grading this order against the
 *     original PO's quantities and prices.
 *   - "Same order, just fixing it" -> save as usual; the original PO stays
 *     the baseline.
 *   - "Cancel" -> nothing is saved; the field keeps its stored value.
 *
 * It never blocks: a save is one click away either way.
 */
import { useState } from 'react';
import styles from './OrderDetailModal.module.css';
import type { HeaderIdentityCheckResponse } from '../../services/portalOrders';

interface Props {
  warning: HeaderIdentityCheckResponse;
  busy?: boolean;
  onCancel: () => void;
  onSameOrder: () => void;
  onDifferentOrder: (note: string) => void;
}

function originLabel(w: HeaderIdentityCheckResponse): string {
  const parts: string[] = [];
  if (w.origin_po_number) parts.push(`PO ${w.origin_po_number}`);
  if (w.origin_customer_no) parts.push(`customer ${w.origin_customer_no}`);
  return parts.length ? parts.join(' for ') : 'the extracted order';
}

export default function HeaderIdentityWarningModal({
  warning,
  busy = false,
  onCancel,
  onSameOrder,
  onDifferentOrder,
}: Props) {
  const [note, setNote] = useState('');
  const fieldLabel = warning.field === 'customer_no' ? 'customer' : 'PO number';

  return (
    <div
      className={styles.confirmOverlay}
      onClick={() => { if (!busy) onCancel(); }}
      data-testid="header-identity-warning"
      role="dialog"
      aria-modal="true"
      aria-labelledby="header-identity-warning-title"
    >
      <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
        <div className={styles.confirmTitle} id="header-identity-warning-title">
          Warning: this doesn&apos;t match the PO this draft came from
        </div>
        <p className={styles.confirmText}>
          This draft was generated from <strong>{originLabel(warning)}</strong>.
          The {fieldLabel} you entered, <strong>{warning.proposed_value}</strong>,
          doesn&apos;t match it.
        </p>
        <p className={styles.confirmText}>
          <strong>Is this a different order?</strong> If it is, say so and the order will
          stop being checked against the original PO&apos;s quantities and prices. If you
          are only correcting the {fieldLabel} on the same order, keep going.
        </p>
        <textarea
          className={styles.confirmTextarea}
          placeholder="Optional note (e.g. second PO attached to the same email)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          disabled={busy}
          data-testid="header-identity-note"
        />
        <div className={styles.confirmActions}>
          <button
            className={`${styles.actionBtn} ${styles.actionCancel}`}
            onClick={onCancel}
            disabled={busy}
            type="button"
            data-testid="header-identity-cancel"
          >
            Cancel
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionCancel}`}
            onClick={onSameOrder}
            disabled={busy}
            type="button"
            data-testid="header-identity-same-order"
          >
            Same order, just fixing it
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionDismiss}`}
            onClick={() => onDifferentOrder(note.trim())}
            disabled={busy}
            type="button"
            data-testid="header-identity-different-order"
          >
            {busy ? 'Saving...' : 'Yes, different order'}
          </button>
        </div>
      </div>
    </div>
  );
}
